import { useState, useMemo } from "react";
import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Heading,
  Icon,
  Input,
} from "@elaniintc/goes-design-system";
import { TypeValueInput } from "../../data-center/components/ValueInput";
import { useExpedienteNode } from "../context";
import { type ValueColumn, type FieldExpirationConfig } from "../types";
import type { Variable } from "../../types";
import type { Variable as IfVariable } from "../../if/types";
import { ExpirationModal } from "../components/expiration-modal";
import { PulseIndicator } from "@/components/common/pulse-indicator";
import {
  DropdownList,
  type IDropdownListItem,
  type DropdownListGroup,
} from "@/components/ui/dropdown-list/dropdown-list";
import { nodeVariableToGroups } from "../../types";
import { filterVariableGroups } from "./utils";

// Special value for "Delete" option - sends empty string to clear the field value
const DELETE_OPTION_VALUE = "__DELETE__";

/**
 * Delete option group to add at the beginning of variable dropdowns
 * This option allows users to clear/delete the field value in the backend
 */
const createDeleteOptionGroup = (): DropdownListGroup => ({
  groupId: "delete-action",
  groupLabels: [],
  items: [
    {
      value: DELETE_OPTION_VALUE,
      label: <span className="text-danger-active">Eliminar</span>,
      icon: { type: "far", name: "trash" },
      type: "delete",
    },
  ],
  icon: { type: "far", name: "trash", variant: "danger" },
});

/**
 * Maps ValueInputType to an array of compatible variable types for filtering
 * Used for DropdownList variable filtering - includes alternative type names
 */
const getCompatibleVariableTypes = (valueInputType: string): string[] => {
  switch (valueInputType) {
    case "string":
    case "regex":
      return ["string", "str", "text", "file"];
    case "number":
    case "positive-integer":
      return ["number", "num", "integer", "float", "int"];
    case "date":
      return ["date", "datetime"];
    case "boolean":
      return ["boolean", "bool"];
    case "variable":
      return []; // No filter for generic variable type - show all
    default:
      return []; // No filter - show all
  }
};

export const ExpedienteUpdateForm = () => {
  const {
    columns,
    setColumns,
    registryName,
    setRegistryName,
    registryNameInputType,
    setRegistryNameInputType,
    registryNameVariable,
    setRegistryNameVariable,
    allInputVariables: inputVariables,
  } = useExpedienteNode();

  // State for expiration modal
  const [expirationModalOpen, setExpirationModalOpen] = useState(false);
  const [selectedColumnForExpiration, setSelectedColumnForExpiration] =
    useState<ValueColumn | null>(null);

  // Search state for registry name variable field
  const [registryNameSearch, setRegistryNameSearch] = useState("");
  const [columnSearches, setColumnSearches] = useState<Record<string, string>>(
    {},
  );

  // State to control which dropdown is open (only one at a time)
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);

  // Convert inputVariables to DropdownList format (for registry name)
  const variableGroups = useMemo(
    () => nodeVariableToGroups(inputVariables),
    [inputVariables],
  );

  // Filter variable groups based on search query for registry name
  const filteredRegistryNameGroups = useMemo(
    () => filterVariableGroups(variableGroups, registryNameSearch),
    [variableGroups, registryNameSearch],
  );

  // Memoize compatible types calculation for each column
  const getColumnCompatibleTypes = useMemo(() => {
    const cache = new Map<string, string[]>();
    return (valueType: string) => {
      if (!cache.has(valueType)) {
        cache.set(valueType, getCompatibleVariableTypes(valueType));
      }
      return cache.get(valueType)!;
    };
  }, []);

  const updateColumninputType = (
    name: string,
    inputType: "input" | "variable",
  ) => {
    setColumns(
      columns.map((col: ValueColumn) =>
        col.name === name ? { ...col, inputType, value: "" } : col,
      ),
    );
    // Close dropdown when changing input type
    setOpenDropdownKey(null);
  };

  const handleOpenExpirationModal = (column: ValueColumn) => {
    setSelectedColumnForExpiration(column);
    setExpirationModalOpen(true);
  };

  const handleSaveExpiration = (config: FieldExpirationConfig) => {
    if (!selectedColumnForExpiration) return;
    setColumns(
      columns.map((col: ValueColumn) =>
        col.name === selectedColumnForExpiration.name
          ? { ...col, expiration: config }
          : col,
      ),
    );
    setSelectedColumnForExpiration(null);
  };

  const handleDeleteExpiration = () => {
    if (!selectedColumnForExpiration) return;
    setColumns(
      columns.map((col: ValueColumn) =>
        col.name === selectedColumnForExpiration.name
          ? { ...col, expiration: undefined }
          : col,
      ),
    );
    setSelectedColumnForExpiration(null);
  };

  return (
    <div className="w-full">
      <div className="w-full pt-3 pb-6 px-4 border-b border-neutral-quiet flex flex-col gap-2">
        <Heading variant="h6">Datos para actualizar</Heading>
        <div className="flex items-end gap-2 w-full">
          <GroupTextField
            label="Nombre del registro"
            labelHelperText="Opcional"
            className="flex-1"
          >
            <GroupTextFieldRow>
              <Button
                variant="quiet"
                type="button"
                className="font-mono"
                group="left"
                onClick={() =>
                  setRegistryNameInputType(
                    registryNameInputType === "inp" ? "var" : "inp",
                  )
                }
              >
                {registryNameInputType === "inp" ? "inp" : "var"}
              </Button>
              {registryNameInputType === "inp" ? (
                <Input
                  group="right"
                  className="flex-1"
                  placeholder="Escribe un texto"
                  value={registryName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setRegistryName(e.target.value)
                  }
                />
              ) : (
                <DropdownList
                  options={filteredRegistryNameGroups}
                  selectedValue={
                    registryNameVariable
                      ? filteredRegistryNameGroups
                          .flatMap((g) => g.items)
                          .find(
                            (item) => item.value === registryNameVariable.id,
                          )
                      : undefined
                  }
                  toggleSelection={(item: IDropdownListItem) => {
                    const variable = inputVariables
                      .flatMap((nv) => nv.variables)
                      .find((v) => v.id === item.value);
                    if (variable)
                      setRegistryNameVariable(variable as IfVariable);
                    setOpenDropdownKey(null);
                  }}
                  placeholder="Selecciona una variable"
                  search={{
                    placeholder: "Buscar variable...",
                    query: registryNameSearch,
                    onSearch: setRegistryNameSearch,
                  }}
                  group="right"
                  widthClassName="flex-1"
                  open={openDropdownKey === "registry-name"}
                  onOpenChange={(open) =>
                    setOpenDropdownKey(open ? "registry-name" : null)
                  }
                />
              )}
            </GroupTextFieldRow>
          </GroupTextField>
          <div className="mb-1 min-w-11 h-11 shrink-0" />
        </div>
        {columns.map((column: ValueColumn) => {
          // Filter variables by compatible types for this column
          const compatibleTypes = getColumnCompatibleTypes(column.valueType);
          const filteredVariableGroups =
            compatibleTypes.length === 0
              ? variableGroups
              : variableGroups
                  .map((group) => ({
                    ...group,
                    items: group.items.filter((item) =>
                      compatibleTypes.includes(item.type?.toLowerCase() || ""),
                    ),
                  }))
                  .filter((group) => group.items.length > 0);

          // Apply search filter to already filtered variable groups
          const searchFilteredGroups = filterVariableGroups(
            filteredVariableGroups,
            columnSearches[column.name] || "",
          );

          // Add "delete" option at the beginning (only if not searching)
          const optionsWithDelete = !columnSearches[column.name]
            ? [createDeleteOptionGroup(), ...searchFilteredGroups]
            : searchFilteredGroups;

          // Check if value is marked for deletion
          const isMarkedForDeletion =
            column.value === "__DELETE__" && column.inputType === "variable";

          return (
            <div key={column.name} className="flex items-end gap-2 w-full">
              <GroupTextField
                label={column.label}
                required={column.isRequired}
                labelHelperText={column.isRequired ? "" : "Opcional"}
                className="flex-1"
              >
                <GroupTextFieldRow>
                  <Button
                    variant="quiet"
                    type="button"
                    className="font-mono"
                    group="left"
                    onClick={() =>
                      updateColumninputType(
                        column.name,
                        column.inputType === "input" ? "variable" : "input",
                      )
                    }
                  >
                    {column.inputType === "input" ? "inp" : "var"}
                  </Button>
                  {column.inputType === "input" ? (
                    <TypeValueInput
                      inputType={column.inputType}
                      valueType={column.valueType}
                      value={column.value ?? ""}
                      placeholder={column.placeholder}
                      onValueChange={(value: string | Variable) =>
                        setColumns(
                          columns.map((col: ValueColumn) =>
                            col.name === column.name ? { ...col, value } : col,
                          ),
                        )
                      }
                      inputVariables={inputVariables}
                      group="right"
                    />
                  ) : (
                    <DropdownList
                      options={optionsWithDelete}
                      selectedValue={
                        isMarkedForDeletion
                          ? {
                              value: DELETE_OPTION_VALUE,
                              label: (
                                <span className="text-danger-active">
                                  Eliminar
                                </span>
                              ),
                              icon: { type: "far", name: "trash" },
                              type: "delete",
                            }
                          : typeof column.value !== "string" && column.value
                            ? searchFilteredGroups
                                .flatMap((g) => g.items)
                                .find(
                                  (item) =>
                                    item.value ===
                                    (column.value as Variable).id,
                                )
                            : undefined
                      }
                      toggleSelection={(item: IDropdownListItem) => {
                        // Handle "delete" option - sets value to marker
                        if (item.value === DELETE_OPTION_VALUE) {
                          setColumns(
                            columns.map((col: ValueColumn) =>
                              col.name === column.name
                                ? { ...col, value: "__DELETE__" }
                                : col,
                            ),
                          );
                          setOpenDropdownKey(null);
                          return;
                        }

                        const variable = inputVariables
                          .flatMap((nv) => nv.variables)
                          .find((v) => v.id === item.value);
                        if (variable) {
                          setColumns(
                            columns.map((col: ValueColumn) =>
                              col.name === column.name
                                ? { ...col, value: variable }
                                : col,
                            ),
                          );
                        }
                        setOpenDropdownKey(null);
                      }}
                      placeholder="Selecciona una variable"
                      search={{
                        placeholder: "Buscar variable...",
                        query: columnSearches[column.name] || "",
                        onSearch: (query) =>
                          setColumnSearches((prev) => ({
                            ...prev,
                            [column.name]: query,
                          })),
                      }}
                      group="right"
                      widthClassName="flex-1"
                      open={openDropdownKey === `column-${column.name}`}
                      onOpenChange={(open) =>
                        setOpenDropdownKey(
                          open ? `column-${column.name}` : null,
                        )
                      }
                    />
                  )}
                </GroupTextFieldRow>
              </GroupTextField>
              <Button
                variant="quiet"
                type="button"
                className="mb-1 border border-neutral rounded-lg min-w-11 h-11 flex items-center justify-center shrink-0 relative"
                onClick={() => handleOpenExpirationModal(column)}
                aria-label="Configurar caducidad"
              >
                <Icon type="far" name="timer" />
                <PulseIndicator showIndicator={!!column.expiration} />
              </Button>
            </div>
          );
        })}
      </div>

      {/* Expiration Configuration Modal */}
      <ExpirationModal
        isOpen={expirationModalOpen}
        onClose={() => {
          setExpirationModalOpen(false);
          setSelectedColumnForExpiration(null);
        }}
        onSave={handleSaveExpiration}
        onDelete={handleDeleteExpiration}
        fieldName={selectedColumnForExpiration?.label ?? ""}
        initialConfig={selectedColumnForExpiration?.expiration}
        fieldValueType={selectedColumnForExpiration?.valueType}
        variables={inputVariables}
      />
    </div>
  );
};
